import { addResponseStatusToSpan } from '../../../utils/httpTracing';
import type { DateTime } from 'effect';
import { Effect, Request, RequestResolver } from 'effect';

import type { ApiInfrastructureErrors } from '../../../data/error/api';
import type { BackendApiErrorUnion } from '../../../data/error/backendApi/_internal/createBackendApiError';
import { createBackendApiError } from '../../../data/error/backendApi/_internal/createBackendApiError';
import { liftRequest } from '../context';
import { ProductFavouriteListCreateResponse } from '@vs-shop/schemas/branded/MiscSchema';
import { schemaBodyJsonClean } from '@vs-shop/schemas/transform/schemaBodyJsonClean';
import { BackendApi } from '../../../service/api/BackendApi';
import type { AuthToken } from '../../../service/auth/AuthToken';
import type { LoggerConfig } from '../../../service/logger/LoggerConfig';
import { ApiModule } from '../../../service/logger/ApiModule';
import { logApiOperation } from '../../../utils/logger/apiLogger';
import type * as HttpClient from 'effect/unstable/http/HttpClient';
import * as HttpClientResponse from 'effect/unstable/http/HttpClientResponse';

const ENDPOINT = '/api/v2/shop/product-favorites/list';
const HTTP_METHOD = 'post';

export const CreateFavouriteListError = createBackendApiError({
  domain: 'ProductFavouriteList',
  endpoint: 'Create',
  path: ENDPOINT,
  identifierField: 'name',
} as const);

export type CreateFavouriteListError = BackendApiErrorUnion<typeof CreateFavouriteListError>;

type CreateFavouriteListInput = {
  name: string;
  productVariantCodes?: string[];
};

const postFavouriteListFetch = (data: CreateFavouriteListInput) =>
  Effect.gen(function* () {
    const apiClient = yield* BackendApi;
    return yield* apiClient
      .path(ENDPOINT)
      .method(HTTP_METHOD)
      .accept('application/json', 'application/json')
      .fetch({ body: { ...data, productVariantCodes: data.productVariantCodes ?? [] } });
  }).pipe(
    Effect.withSpan(`http.${HTTP_METHOD} ${ENDPOINT}`, {
      kind: 'client',
      attributes: {
        'http.request.method': HTTP_METHOD,
        'productFavouriteList.name': data.name,
      },
    })
  );

const createFavouriteList = (name: string, productVariantCodes: string[] = []) =>
  postFavouriteListFetch({ name, productVariantCodes }).pipe(
    Effect.map((response) => response.response),
    Effect.tap(addResponseStatusToSpan),
    Effect.flatMap(
      HttpClientResponse.matchStatus({
        '2xx': (response) => schemaBodyJsonClean(ProductFavouriteListCreateResponse)(response),
        404: (response) =>
          Effect.fail(new CreateFavouriteListError.NotFoundError({ name, response })),
        422: CreateFavouriteListError.createViolationError(name),
        orElse: (response) =>
          Effect.fail(new CreateFavouriteListError.ApiRequestError({ name, response })),
      })
    ),
    Effect.tap((result) =>
      logApiOperation(
        ApiModule.ProductFavouriteListCreate,
        'Debug',
        `Successfully created favourite list: ${result.name}`
      )
    ),
    Effect.withSpan('api.productFavouriteList.create', {
      kind: 'internal',
      attributes: {
        'productFavouriteList.name': name,
      },
    })
  );

type CreateFavouriteListRequest = {
  readonly _tag: 'CreateFavouriteListRequest';
  readonly name: string;
  readonly productVariantCodes: string[];
} & Request.Request<
  ProductFavouriteListCreateResponse,
  CreateFavouriteListError | ApiInfrastructureErrors,
  BackendApi | AuthToken | HttpClient.HttpClient | LoggerConfig | DateTime.CurrentTimeZone
>;

const CreateFavouriteListRequest = Request.tagged<CreateFavouriteListRequest>(
  'CreateFavouriteListRequest'
);

const CreateFavouriteListResolver = RequestResolver.fromEffect(
  liftRequest((request: CreateFavouriteListRequest) =>
    createFavouriteList(request.name, request.productVariantCodes)
  )
);

export const requestCreateFavouriteList = (name: string, productVariantCodes: string[] = []) =>
  Effect.request(
    CreateFavouriteListRequest({ name, productVariantCodes }),
    CreateFavouriteListResolver
  );
